import {
  createGroupMember,
  deleteGroupMember,
  getGroupMemberList,
  getGroupMemberListOwner,
  reorderGroupMembers,
  unlinkGroupMemberConnection,
  updateGroupMemberBulk,
  updateGroupMemberPropertyVisible,
  updateGroupMemberStatus,
} from '../../api/groupMemberApi';
import { UpdateGroupMemberBulkReq, UpdateGroupMemberPropertyVisibleReq, UpdateGroupMemberStatusReq, UpdateReorderListReq } from '../../dto/ReqDtoRepository';
import { GroupMemberData, GroupMemberPropertyVisibleData, SocialMedia } from '../../dto/EntityRepository';
import {
  GroupMemberListResponse,
  GroupMemberPropertyVisibleResponse,
  GroupMemberResponse,
} from '../../dto/ResDtoRepository';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useGroupMemberStore } from '../../store/groupMemberStore';
import { useApprovalRequestStore } from '../../store/approvalRequestStore';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';
import { useApiWrapper } from './apiWrapper';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { CountryType, GroupMemberRole, GroupMemberStatus } from '../../enum/EnumRepository';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { toUpdateGroupMemberBulkReq } from '../../util/groupMemberBulk';

interface UseGroupMemberStateResult {
  groupMemberList: GroupMemberData[];
  focusedGroupMemberId: string | null;
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
  hasChanged: boolean;
}

interface UseGroupMemberActionsResult {
  getGroupMemberList: () => Promise<void>;
  getGroupMemberListOwner: () => Promise<void>;
  createGroupMember: () => Promise<void>;
  updateGroupMemberBulk: (data: UpdateGroupMemberBulkReq) => Promise<void>;
  /** hasChanged인 항목만 모아 bulk 수정. 없으면 요청하지 않는다. */
  updateChangedGroupMemberBulk: () => Promise<void>;
  reorderGroupMembers: (data: UpdateReorderListReq) => Promise<void>;
  updateGroupMemberPropertyVisible: (data: UpdateGroupMemberPropertyVisibleReq) => Promise<void>;
  unlinkGroupMember: (groupMemberId: string) => Promise<boolean>;
  updateGroupMemberStatus: (data: UpdateGroupMemberStatusReq) => Promise<void>;
  deleteGroupMember: (groupMemberId: string) => Promise<void>;
  setFocusedGroupMemberId: (groupMemberId: string | null) => void;
  clearGroupMembers: () => void;
}

type UseGroupMemberResult = UseGroupMemberStateResult & UseGroupMemberActionsResult;

const emptySns: SocialMedia = {
  twitter: '',
  instagram: '',
  facebook: '',
  threads: '',
  behance: '',
  youtube: '',
  vimeo: '',
  url1: '',
};

const normalizeGroupMemberPropertyVisible = (
  propertyVisible?: Partial<GroupMemberPropertyVisibleData>,
): GroupMemberPropertyVisibleData => ({
  id: String(propertyVisible?.id ?? ''),
  email: propertyVisible?.email ?? true,
  contact: propertyVisible?.contact ?? true,
  twitter: propertyVisible?.twitter ?? true,
  instagram: propertyVisible?.instagram ?? true,
  facebook: propertyVisible?.facebook ?? true,
  youtube: propertyVisible?.youtube ?? true,
  url1: propertyVisible?.url1 ?? true,
});

export const normalizeGroupMember = (groupMember: Partial<GroupMemberData>): GroupMemberData => ({
  id: String(groupMember.id ?? ''),
  name: groupMember.name ?? '',
  nameEn: groupMember.nameEn ?? '',
  role: groupMember.role ?? GroupMemberRole.ARTIST,
  country: groupMember.country ?? CountryType.NONE,
  year: groupMember.year ?? 0,
  email: groupMember.email ?? '',
  contact: groupMember.contact ?? '',
  description: groupMember.description ?? '',
  descriptionEn: groupMember.descriptionEn ?? '',
  sns: {
    ...emptySns,
    ...(groupMember.sns ?? {}),
  },
  tags: (groupMember.tags ?? []).map((tag) => ({
    id: Number(tag.id),
    name: tag.name ?? '',
    index: Number(tag.index ?? 0),
  })),
  propertyVisible: normalizeGroupMemberPropertyVisible(groupMember.propertyVisible),
  status: groupMember.status ?? GroupMemberStatus.ACTIVE,
  linkedMemberAui: groupMember.linkedMemberAui ?? '',
  linkedMemberHandle: groupMember.linkedMemberHandle ?? '',
  index: groupMember.index ?? 0,
});

export const useGroupMemberState = (): UseGroupMemberStateResult => {
  const groupMemberList = useGroupMemberStore((state) => state.groupMemberList);
  const focusedGroupMemberId = useGroupMemberStore((state) => state.focusedGroupMemberId);
  const listOwnerAui = useGroupMemberStore((state) => state.listOwnerAui);
  const listScope = useGroupMemberStore((state) => state.listScope);
  const status = useGroupMemberStore((state) => state.status);
  const hasChanged = groupMemberList.some((member) => member.hasChanged);

  return {
    groupMemberList,
    focusedGroupMemberId,
    listOwnerAui,
    listScope,
    status,
    hasChanged,
  };
};

export const useGroupMemberActions = (): UseGroupMemberActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const setGroupMemberList = useGroupMemberStore((s) => s.setGroupMemberList);
  const replaceGroupMemberList = useGroupMemberStore((s) => s.replaceGroupMemberList);
  const beginGroupMemberListFetch = useGroupMemberStore((s) => s.beginGroupMemberListFetch);
  const setGroupMemberListError = useGroupMemberStore((s) => s.setGroupMemberListError);
  const addGroupMember = useGroupMemberStore((s) => s.addGroupMember);
  const afterUpdateGroupMemberBulk = useGroupMemberStore((s) => s.afterUpdateGroupMemberBulk);
  const updateGroupMemberPropertyVisibleStore = useGroupMemberStore(
    (s) => s.updateGroupMemberPropertyVisible,
  );
  const updateGroupMemberStatusStore = useGroupMemberStore((s) => s.updateGroupMemberStatus);
  const updateGroupMemberConnectionStore = useGroupMemberStore(
    (s) => s.updateGroupMemberConnection,
  );
  const removeGroupMember = useGroupMemberStore((s) => s.removeGroupMember);
  const removeGroupMemberApprovalRequests = useApprovalRequestStore(
    (s) => s.removeGroupMemberApprovalRequests,
  );
  const setFocusedGroupMemberId = useGroupMemberStore((s) => s.setFocusedGroupMemberId);
  const clearGroupMembers = useGroupMemberStore((s) => s.clearGroupMembers);
  const { setUpdatedTempAlert, setDeletedTempAlert } = useTempAlertStore();
  const withApiHandler = useApiWrapper();

  const handleGetGroupMemberListSuccess = (
    response: GroupMemberListResponse,
    ownerAui: string,
    scope: ResourceScope,
  ) => {
    setGroupMemberList(response.data.map(normalizeGroupMember), ownerAui, scope);
  };

  const handleCreateGroupMemberSuccess = (response: GroupMemberResponse) => {
    const created = normalizeGroupMember(response.data);
    addGroupMember(created);
    setUpdatedTempAlert();
    requestAnimationFrame(() => {
      document.getElementById(`group-member-${created.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const handleUnlinkGroupMemberSuccess = (groupMemberId: string) => {
    updateGroupMemberConnectionStore(groupMemberId, '', '');
    setUpdatedTempAlert();
  };

  const handleUpdateGroupMemberBulkSuccess = (response: GroupMemberListResponse) => {
    afterUpdateGroupMemberBulk(response.data.map(normalizeGroupMember));
    setUpdatedTempAlert();
  };

  const handleReorderGroupMembersSuccess = (response: GroupMemberListResponse) => {
    replaceGroupMemberList(response.data.map(normalizeGroupMember));
    setUpdatedTempAlert();
  };

  const handleUpdateGroupMemberPropertyVisibleSuccess = (
    response: GroupMemberPropertyVisibleResponse,
    data: UpdateGroupMemberPropertyVisibleReq,
  ) => {
    updateGroupMemberPropertyVisibleStore(
      data.id,
      normalizeGroupMemberPropertyVisible(response.data),
    );
    setUpdatedTempAlert();
  };

  const handleUpdateGroupMemberStatusSuccess = (
    response: GroupMemberResponse,
    data: UpdateGroupMemberStatusReq,
  ) => {
    updateGroupMemberStatusStore(
      data.id,
      response.data.status ?? data.status,
    );
    setUpdatedTempAlert();
  };

  const handleDeleteGroupMemberSuccess = (groupMemberId: string) => {
    removeGroupMember(groupMemberId);
    removeGroupMemberApprovalRequests(groupMemberId);
    setDeletedTempAlert();
  };

  const fetchGroupMemberList = async (aui: string, scope: ResourceScope) => {
    beginGroupMemberListFetch(aui, scope);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => (scope === 'owner' ? getGroupMemberListOwner(aui) : getGroupMemberList(aui)),
        (response) => handleGetGroupMemberListSuccess(response, aui, scope),
      );
    } catch (error) {
      setGroupMemberListError();
      throw error;
    }
  };

  const handleGroupMemberRequest = async (
    action:
      | 'get'
      | 'get_for_owner'
      | 'create'
      | 'update bulk'
      | 'reorder'
      | 'update_property_visible'
      | 'update_status'
      | 'delete',
    data?:
      | UpdateGroupMemberBulkReq
      | UpdateReorderListReq
      | UpdateGroupMemberPropertyVisibleReq
      | UpdateGroupMemberStatusReq
      | string,
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'create':
          return runCurrentArchiveRequest(
            aui,
            () => createGroupMember(aui),
            handleCreateGroupMemberSuccess,
          );
        case 'update bulk':
          return runCurrentArchiveRequest(
            aui,
            () => updateGroupMemberBulk(aui, data as UpdateGroupMemberBulkReq),
            handleUpdateGroupMemberBulkSuccess,
          );
        case 'reorder':
          return runCurrentArchiveRequest(
            aui,
            () => reorderGroupMembers(aui, data as UpdateReorderListReq),
            handleReorderGroupMembersSuccess,
          );
        case 'update_property_visible': {
          const request = data as UpdateGroupMemberPropertyVisibleReq;
          return runCurrentArchiveRequest(
            aui,
            () => updateGroupMemberPropertyVisible(aui, request),
            response => handleUpdateGroupMemberPropertyVisibleSuccess(
              response,
              request,
            ),
          );
        }
        case 'update_status': {
          const request = data as UpdateGroupMemberStatusReq;
          return runCurrentArchiveRequest(
            aui,
            () => updateGroupMemberStatus(aui, request),
            response => handleUpdateGroupMemberStatusSuccess(
              response,
              request,
            ),
          );
        }
        case 'delete': {
          const groupMemberId = data as string;
          return runCurrentArchiveRequest(
            aui,
            () => deleteGroupMember(aui, groupMemberId),
            () => handleDeleteGroupMemberSuccess(groupMemberId),
          );
        }
        case 'get_for_owner':
          return fetchGroupMemberList(aui, 'owner');
        case 'get':
        default:
          return fetchGroupMemberList(aui, 'public');
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const getGroupMemberListHandler = () =>
    handleGroupMemberRequest('get');
  const getGroupMemberListOwnerHandler = () =>
    handleGroupMemberRequest('get_for_owner');
  const createGroupMemberHandler = () =>
    handleGroupMemberRequest('create');
  const updateGroupMemberBulkHandler = (data: UpdateGroupMemberBulkReq) =>
    handleGroupMemberRequest('update bulk', data);
  const updateChangedGroupMemberBulkHandler = () => {
    const request = toUpdateGroupMemberBulkReq(
      useGroupMemberStore.getState().groupMemberList,
    );
    if (!request) return Promise.resolve();
    return handleGroupMemberRequest('update bulk', request);
  };
  const reorderGroupMembersHandler = (data: UpdateReorderListReq) =>
    handleGroupMemberRequest('reorder', data);
  const updateGroupMemberPropertyVisibleHandler = (
    data: UpdateGroupMemberPropertyVisibleReq,
  ) => handleGroupMemberRequest('update_property_visible', data);
  const unlinkGroupMemberHandler = async (groupMemberId: string) => {
    if (!pageOwnerAui) return false;
    const aui = pageOwnerAui;
    const response = await withApiHandler(
      () => runCurrentArchiveRequest(
        aui,
        () => unlinkGroupMemberConnection(aui, groupMemberId),
        (result) => result,
      ),
      [aui, 'unlink', groupMemberId],
    );
    if (!response) return false;
    handleUnlinkGroupMemberSuccess(groupMemberId);
    return true;
  };
  const updateGroupMemberStatusHandler = (
    data: UpdateGroupMemberStatusReq,
  ) => handleGroupMemberRequest('update_status', data);
  const deleteGroupMemberHandler = (groupMemberId: string) =>
    handleGroupMemberRequest('delete', groupMemberId);

  return {
    getGroupMemberList: getGroupMemberListHandler,
    getGroupMemberListOwner: getGroupMemberListOwnerHandler,
    createGroupMember: createGroupMemberHandler,
    updateGroupMemberBulk: updateGroupMemberBulkHandler,
    updateChangedGroupMemberBulk: updateChangedGroupMemberBulkHandler,
    reorderGroupMembers: reorderGroupMembersHandler,
    updateGroupMemberPropertyVisible: updateGroupMemberPropertyVisibleHandler,
    unlinkGroupMember: unlinkGroupMemberHandler,
    updateGroupMemberStatus: updateGroupMemberStatusHandler,
    deleteGroupMember: deleteGroupMemberHandler,
    setFocusedGroupMemberId,
    clearGroupMembers,
  };
};

/** 기존 호출부 호환: state + actions */
export const useGroupMember = (): UseGroupMemberResult => ({
  ...useGroupMemberState(),
  ...useGroupMemberActions(),
});
